import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Badge } from 'components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { 
  ArrowLeft, Award, Plus, Calendar, Building, 
  CheckCircle, AlertTriangle, Clock, Trash2, RefreshCw, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const COMMON_CERTIFICATIONS = [
  { name: 'CPR/BLS', issuer: 'American Heart Association' },
  { name: 'First Aid', issuer: 'American Red Cross' },
  { name: 'CNA - Certified Nursing Assistant', issuer: 'State Board' },
  { name: 'HHA - Home Health Aide', issuer: 'State Board' },
  { name: 'Dementia Care Certification', issuer: 'Various' },
  { name: "Alzheimer's Care Training", issuer: "Alzheimer's Association" },
  { name: 'Medication Administration', issuer: 'State Board' },
  { name: 'Food Handler Certification', issuer: 'Health Department' }
];

export default function Certifications() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    issuing_organization: '',
    issue_date: '',
    expiry_date: ''
  });

  useEffect(() => {
    if (user?.user_id) {
      fetchCertifications();
    }
  }, [user?.user_id]);

  const fetchCertifications = async () => {
    try {
      const response = await api.get(`/caregivers/${user.user_id}/certifications`);
      setCertifications(response.data.certifications);
    } catch (error) {
      console.error('Failed to fetch certifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post(`/caregivers/${user.user_id}/certifications`, formData);
      toast.success('Certification added!');
      setDialogOpen(false);
      setFormData({ name: '', issuing_organization: '', issue_date: '', expiry_date: '' });
      fetchCertifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add certification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (certId) => {
    if (!confirm('Are you sure you want to delete this certification?')) return;
    
    try {
      await api.delete(`/caregivers/${user.user_id}/certifications/${certId}`);
      toast.success('Certification deleted');
      fetchCertifications();
    } catch (error) {
      toast.error('Failed to delete certification');
    }
  };

  const handleQuickAdd = (cert) => {
    setFormData({
      name: cert.name,
      issuing_organization: cert.issuer,
      issue_date: '',
      expiry_date: ''
    });
  };

  const getStatusBadge = (cert) => {
    const now = new Date();
    const expiry = cert.expiry_date ? new Date(cert.expiry_date) : null;
    const isExpired = expiry && expiry < now;

    if (isExpired) {
      return <Badge className="bg-rose-100 text-rose-700 text-[10px] sm:text-xs"><AlertTriangle className="w-3 h-3" />Expired</Badge>;
    }
    if (cert.verified) {
      return <Badge className="bg-teal-100 text-teal-700 text-[10px] sm:text-xs"><CheckCircle className="w-3 h-3" />Verified</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700 text-[10px] sm:text-xs"><Clock className="w-3 h-3" />Pending</Badge>;
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expDate = new Date(expiryDate);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expDate <= thirtyDaysFromNow && expDate > new Date();
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="back-to-settings">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']" data-testid="certifications-title">My Certifications</h1>
              <p className="text-xs sm:text-sm text-slate-500">Manage your professional certifications</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="shrink-0 bg-teal-500 hover:bg-teal-600" data-testid="add-certification-btn">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Add</span> Certification
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Add Certification</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                {/* Quick Add Buttons */}
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Quick Add Common Certifications</Label>
                  <div className="flex flex-wrap gap-2">
                    {COMMON_CERTIFICATIONS.slice(0, 4).map((cert) => (
                      <Button
                        key={cert.name}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAdd(cert)}
                        className="text-xs"
                      >
                        {cert.name.split(' - ')[0]}
                      </Button>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="cert-name">Certification Name *</Label>
                    <Input
                      id="cert-name"
                      placeholder="e.g., CPR/BLS, CNA"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      data-testid="cert-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cert-issuer">Issuing Organization</Label>
                    <Input
                      id="cert-issuer"
                      placeholder="e.g., American Red Cross"
                      value={formData.issuing_organization}
                      onChange={(e) => setFormData({...formData, issuing_organization: e.target.value})}
                      data-testid="cert-issuer-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="issue-date">Issue Date</Label>
                      <Input
                        id="issue-date"
                        type="date"
                        value={formData.issue_date}
                        onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                        data-testid="cert-issue-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="exp-date">Expiration Date</Label>
                      <Input
                        id="exp-date"
                        type="date"
                        value={formData.expiry_date}
                        onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                        data-testid="cert-exp-date"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-teal-500 hover:bg-teal-600"
                    disabled={submitting}
                    data-testid="save-certification-btn"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Add Certification
                  </Button>
                </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        {/* Expiring Soon Alert */}
        {certifications.some(c => isExpiringSoon(c.expiry_date)) && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Certifications Expiring Soon</h4>
              <p className="text-sm text-amber-700">
                Some of your certifications will expire within 30 days. Renew them to maintain your verified status.
              </p>
            </div>
          </div>
        )}

        {/* Certifications List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Award className="w-5 h-5 text-teal-500" />
              Your Certifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {certifications.length === 0 ? (
              <div className="text-center py-12">
                <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Certifications Added</h3>
                <p className="text-slate-500 mb-4">Add your professional certifications to build trust with families</p>
                <Button onClick={() => setDialogOpen(true)} className="bg-teal-500 hover:bg-teal-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Certification
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {certifications.map((cert) => (
                  <div 
                    key={cert.certification_id} 
                    className={`p-3 sm:p-4 rounded-xl border ${
                      isExpired(cert.expiry_date) ? 'bg-rose-50 border-rose-200' :
                      isExpiringSoon(cert.expiry_date) ? 'bg-amber-50 border-amber-200' :
                      'bg-slate-50 border-slate-200'
                    }`}
                    data-testid={`cert-${cert.certification_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 ${
                        isExpired(cert.expiry_date) ? 'bg-rose-100' :
                        cert.verified ? 'bg-teal-100' : 'bg-slate-100'
                      }`}>
                        <Award className={`w-5 h-5 sm:w-6 sm:h-6 ${
                          isExpired(cert.expiry_date) ? 'text-rose-500' :
                          cert.verified ? 'text-teal-500' : 'text-slate-500'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-sm sm:text-base leading-tight">{cert.name}</h4>
                          <div className="flex items-center gap-1 shrink-0">
                            {getStatusBadge(cert)}
                          </div>
                        </div>
                        {cert.issuing_organization && (
                          <p className="text-xs sm:text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                            <Building className="w-3 h-3 shrink-0" />
                            {cert.issuing_organization}
                          </p>
                        )}
                        <div className="flex items-center gap-3 sm:gap-4 mt-1.5 text-xs sm:text-sm flex-wrap">
                          {cert.issue_date && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <Calendar className="w-3 h-3 shrink-0" />
                              Issued: {new Date(cert.issue_date).toLocaleDateString()}
                            </span>
                          )}
                          {cert.expiry_date && (
                            <span className={`flex items-center gap-1 ${
                              isExpired(cert.expiry_date) ? 'text-rose-600' :
                              isExpiringSoon(cert.expiry_date) ? 'text-amber-600' : 'text-slate-500'
                            }`}>
                              <Clock className="w-3 h-3 shrink-0" />
                              Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(cert.certification_id)}
                        className="text-slate-400 hover:text-rose-500 h-7 text-xs"
                          data-testid={`delete-cert-${cert.certification_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips Card */}
        <Card className="mt-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium mb-1">Why Add Certifications?</h4>
                <ul className="text-sm text-slate-500 space-y-1">
                  <li>• Verified certifications display a badge on your profile</li>
                  <li>• Families are more likely to hire caregivers with relevant certifications</li>
                  <li>• Some care positions require specific certifications</li>
                  <li>• Track expiration dates to stay current</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
